import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircle, Cancel, Warning, Calculate } from '@mui/icons-material';

/**
 * Prep-mecanique blank QC — captures masse initiale / masse passante and
 * computes the % perte de masse used as the contamination/loss check.
 *
 *   Concassage : (Mi − Mp) / Mi * 100  must be < 15 %
 *   Broyage    : (Mi − Mp) / Mi * 100  must be < 30 %
 *
 * The result is exposed via onChange so the parent can persist it under
 * metadonnees.prep_blank_concassage / prep_blank_broyage when completing
 * the corresponding step.
 */

export type PrepBlankStep = 'CONCASSAGE' | 'BROYAGE';

export interface PrepBlankData {
  step: PrepBlankStep;
  masse_initiale_g: number;
  masse_passante_g: number;
  percent_loss: number;
  threshold_percent: number;
  status: 'CONFORME' | 'WARNING' | 'NON_CONFORME' | 'INVALID';
  notes?: string;
}

interface Props {
  step: PrepBlankStep;
  threshold: number; // 15 (concassage) or 30 (broyage)
  initial?: Partial<PrepBlankData>;
  onChange?: (data: PrepBlankData | null) => void;
  disabled?: boolean;
  /** Optional header override (e.g. "QC Qualité — Broyage" for the per-sample
   *  quality check). Defaults to the blank-préparation wording. */
  title?: string;
}

const STEP_LABEL: Record<PrepBlankStep, string> = {
  CONCASSAGE: 'Concassage',
  BROYAGE: 'Broyage',
};

const PrepBlankSubForm: React.FC<Props> = ({
  step,
  threshold,
  initial,
  onChange,
  disabled,
  title,
}) => {
  const [mi, setMi] = useState<string>(
    initial?.masse_initiale_g != null ? String(initial.masse_initiale_g) : '',
  );
  const [mp, setMp] = useState<string>(
    initial?.masse_passante_g != null ? String(initial.masse_passante_g) : '',
  );
  const [notes, setNotes] = useState<string>(initial?.notes || '');

  const computed = useMemo<PrepBlankData | null>(() => {
    const miNum = Number(mi);
    const mpNum = Number(mp);
    if (!Number.isFinite(miNum) || miNum <= 0) return null;
    if (!Number.isFinite(mpNum) || mpNum < 0) return null;
    if (mpNum > miNum) {
      return {
        step,
        masse_initiale_g: miNum,
        masse_passante_g: mpNum,
        percent_loss: 0,
        threshold_percent: threshold,
        status: 'INVALID',
        notes,
      };
    }
    const pct = ((miNum - mpNum) / miNum) * 100;
    const rounded = Math.round(pct * 100) / 100;
    const warningBand = (threshold * 2) / 3;
    let status: PrepBlankData['status'] = 'CONFORME';
    if (rounded >= threshold) status = 'NON_CONFORME';
    else if (rounded >= warningBand) status = 'WARNING';
    return {
      step,
      masse_initiale_g: miNum,
      masse_passante_g: mpNum,
      percent_loss: rounded,
      threshold_percent: threshold,
      status,
      notes,
    };
  }, [mi, mp, notes, step, threshold]);

  useEffect(() => {
    onChange?.(computed);
  }, [computed, onChange]);

  const isInvalid = computed?.status === 'INVALID';
  const isOk = computed?.status === 'CONFORME';
  const isWarn = computed?.status === 'WARNING';
  const isNok = computed?.status === 'NON_CONFORME';

  const statusColor = isOk
    ? 'success'
    : isWarn
    ? 'warning'
    : isNok
    ? 'error'
    : 'default';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Calculate color="primary" />
        <Typography variant="h6">
          {title || `QC Blanc Préparation — ${STEP_LABEL[step]}`}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={`Seuil: < ${threshold} %`}
          sx={{ ml: 'auto' }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Saisissez la masse initiale et la masse passante. Le pourcentage de
        perte est calculé automatiquement avec la formule&nbsp;
        <code>(Mi − Mp) / Mi × 100</code>. Le résultat doit rester strictement
        inférieur au seuil pour valider l'étape.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          type="number"
          label="Masse initiale (g)"
          value={mi}
          disabled={disabled}
          onChange={(e) => setMi(e.target.value)}
          inputProps={{ min: 0, step: 0.01 }}
          required
        />
        <TextField
          fullWidth
          type="number"
          label="Masse passante (g)"
          value={mp}
          disabled={disabled}
          onChange={(e) => setMp(e.target.value)}
          inputProps={{ min: 0, step: 0.01 }}
          required
          error={isInvalid}
          helperText={isInvalid ? 'Doit être ≤ masse initiale' : undefined}
        />
      </Stack>

      <Box sx={{ mt: 2 }}>
        {computed && !isInvalid ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                % Perte de masse
              </Typography>
              <Typography
                variant="h4"
                color={`${statusColor}.main`}
                sx={{ fontWeight: 700 }}
              >
                {computed.percent_loss.toFixed(2)} %
              </Typography>
            </Box>
            <Tooltip
              title={
                isOk
                  ? `Conforme — perte sous le seuil de ${threshold} %`
                  : isWarn
                  ? `Zone d'alerte — supérieur à ${(
                      (threshold * 2) /
                      3
                    ).toFixed(1)} %`
                  : `Non conforme — dépasse le seuil de ${threshold} %`
              }
            >
              <Chip
                color={statusColor as any}
                icon={
                  isOk ? (
                    <CheckCircle />
                  ) : isWarn ? (
                    <Warning />
                  ) : (
                    <Cancel />
                  )
                }
                label={
                  isOk
                    ? 'CONFORME'
                    : isWarn
                    ? 'ALERTE'
                    : 'NON CONFORME'
                }
                sx={{ fontWeight: 700, px: 1.5 }}
              />
            </Tooltip>
          </Stack>
        ) : (
          <Alert severity={isInvalid ? 'error' : 'info'}>
            {isInvalid
              ? 'La masse passante ne peut pas dépasser la masse initiale.'
              : 'Saisissez les masses pour calculer le pourcentage de perte.'}
          </Alert>
        )}
      </Box>

      {isNok && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Le blanc dépasse le seuil de {threshold} %. La carte de contrôle
          sera marquée hors limites — prévenez le superviseur du laboratoire.
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 2 }}
        label="Notes / observations"
        value={notes}
        disabled={disabled}
        onChange={(e) => setNotes(e.target.value)}
      />
    </Paper>
  );
};

export default PrepBlankSubForm;
